import React, { FormEvent, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  BannerPrice,
  ButtonDS,
  DatePicker,
  GlobalIcon,
  ListSection,
  StaticImgText,
  StepBar,
  Typography,
  UpsellIconSection,
} from "get-life-storybook-ts";
import { GetlifeContext } from "../../context/GetLifeContext.context";
import { I18nContext } from "../../context/i18n.context";
import HttpLeadRepository from "../../api/request/Lead/Lead.service";
import { patchOrganicOffer, readOrganicOffer } from "../../native/organicOffer";
import { DayAvailable, DaysAvailable } from "../../utils/InternationlChanges";
import TrustpilotReviews from "../NewUpsellV2/Components/TrustpilotReviews/TrustpilotReviews";
import {
  ITComplianceOnly,
  IntermediaryBadge,
  PriceTransparencyNote,
  PrecontractualDocs,
  WithdrawalRightNotice,
} from "../../components/Compliance";
import { IconSection, Truspilot, stepList } from "../../utils/dataForNewUpsell";
import { GlobalShowTruspilot } from "../../utils/InternationlChanges";
import { DOMAIN } from "../../constants/Global";
import { trackSubmissionError } from "../../helpers/Tracking";
import type { UpsellCustomResult } from "../../helpers/Tracking/events";
import { UpsellVisit } from "../../helpers/Tracking/upsell";
import {
  CapitalCheck,
  CapitalLimits,
  DEFAULT_CAPITAL_LIMITS,
  checkCapital,
  formatCapitalEuro,
  formatCapitalNumber,
  parseCapitalInput,
  readCapitalLimits,
  stepCapital,
} from "./capitalInput";
import "../NewUpsellV2/css/newUpsell.scss";
import "./NativeUpsell.styles.scss";

/**
 * Upsell ORGANIC native (F5c). AGL es producto único → 3 capitales (el inicial
 * ±25.000 €): el gateway re-prepara el quote con las 3 ofertas y el cliente
 * elige una (select + selection-lock). Reusa el chrome y el CONTENIDO del upsell
 * legacy (/payment/1): hero, StepBar, coberturas, iconos, texto estático,
 * bloques legales IT y Trustpilot; lo nuevo es la selección de capital (no el
 * slider de NewUpsellV2). Solo se monta en modo native (lo decide PaymentGatewayV2).
 */

interface UpsellOption {
  pricingId: string;
  capital: number | null;
  firstMonthlyCents: number | null;
  rateStatus?: string | null;
  // Con 10_sconto: firstMonthlyCents ya viene descontado; regular* = precio
  // actuarial sin descuento (para el tachado).
  regularFirstMonthlyCents?: number | null;
  discountBps?: number;
}

/**
 * getTranslate del i18n devuelve la propia clave cuando falta la traducción
 * (`translations[lang].default[key] || key`), así que el `|| fallback` normal
 * queda muerto. t() trata el eco-de-clave como ausente y cae al copy interino.
 */
const IT_FALLBACK: Record<string, string> = {
  "organic.upsell.title": "Scegli il capitale assicurato",
  "organic.upsell.subtitle":
    "Puoi aumentare o ridurre il capitale rispetto a quello iniziale. Scegli l'opzione più adatta a te.",
  "organic.upsell.summaryTitle": "Il tuo prezzo",
  "organic.upsell.perMonth": "/mese",
  "organic.upsell.continue": "Continua",
  "organic.upsell.capitalLabel": "Capitale",
  "organic.upsell.cancelAnytime": "Puoi disdire quando vuoi, senza penali.",
  "organic.upsell.insteadOf": "invece di",
  "organic.upsell.recommended": "Più scelto",
  "organic.upsell.selected": "Selezionato",
  "organic.upsell.choose": "Scegli",
  "newUpsell.banner.subtitle": "La tua protezione da {{capital}} €",
  // Capital concreto (30-oct-2026): pendientes de alta en getlife/translations.
  "organic.upsell.custom.title": "Vuoi un capitale diverso?",
  "organic.upsell.custom.label": "Capitale assicurato",
  "organic.upsell.custom.hint": "Da {{min}} a {{max}}, a multipli di {{step}}.",
  "organic.upsell.custom.apply": "Calcola il prezzo",
  "organic.upsell.custom.decrease": "Diminuisci di {{step}}",
  "organic.upsell.custom.increase": "Aumenta di {{step}}",
  "organic.upsell.custom.error.invalid": "Inserisci un importo valido, ad esempio 125.000.",
  "organic.upsell.custom.error.min": "Il capitale minimo è {{min}}.",
  "organic.upsell.custom.error.max": "Il capitale massimo è {{max}}.",
  "organic.upsell.custom.error.request": "Non siamo riusciti a calcolare il prezzo. Riprova tra qualche istante.",
  "organic.upsell.custom.adjusted":
    "Abbiamo arrotondato il capitale a {{capital}}: si può scegliere solo a multipli di {{step}}.",
  "organic.upsell.custom.applied": "Prezzo aggiornato per {{capital}}.",
};

const HOME_IMAGE =
  "https://storage.googleapis.com/getlife-bucket-1/public/image/img-new-web/Illustration_woman.svg";

const useT = () => {
  const {
    state: { translate },
  } = useContext(I18nContext);
  return (key: string): string => {
    const value = translate(key);
    if (value && value !== key) return value;
    return IT_FALLBACK[key] ?? key;
  };
};

const capitalLabel = (capital: number | null): string =>
  null === capital ? "—" : `${capital.toLocaleString("it-IT")} €`;

/** Parte entera y decimales (2 díg.) de un importe en céntimos. */
const splitCents = (cents: number | null): { integer: string; decimals: string } => {
  if (null === cents) return { integer: "—", decimals: "00" };
  const euros = cents / 100;
  const integer = Math.floor(euros).toLocaleString("it-IT");
  const decimals = Math.round((euros - Math.floor(euros)) * 100)
    .toString()
    .padStart(2, "0");
  return { integer, decimals };
};

const perMonthEuro = (cents: number | null): string => {
  const { integer, decimals } = splitCents(cents);
  return "—" === integer ? "—" : `${integer},${decimals} €`;
};

/** Capital libre rechazado en el front → `result` de upsell_custom_applied. */
const CUSTOM_REJECTION: Record<Exclude<CapitalCheck["kind"], "ok">, UpsellCustomResult> = {
  invalid: "invalid",
  belowMin: "below_min",
  aboveMax: "above_max",
};

/** El mensual en euros (lo que enseña la tarjeta), o null. */
const monthlyEuros = (cents: number | null): number | null => ("number" === typeof cents ? cents / 100 : null);

/** Hay 10_sconto cuando el gateway expone un precio regular por encima del cobrado. */
const hasDiscount = (opt: UpsellOption): boolean =>
  "number" === typeof opt.regularFirstMonthlyCents &&
  "number" === typeof opt.firstMonthlyCents &&
  opt.regularFirstMonthlyCents > opt.firstMonthlyCents;

/** % de descuento redondeado (para el chip «-10%»), o null si no hay. */
const discountPct = (opt: UpsellOption): number | null => {
  if (!hasDiscount(opt) || "number" !== typeof opt.regularFirstMonthlyCents || 0 === opt.regularFirstMonthlyCents) {
    return null;
  }
  return Math.round((1 - (opt.firstMonthlyCents ?? 0) / opt.regularFirstMonthlyCents) * 100);
};

export default function NativeUpsell(): JSX.Element {
  const navigate = useNavigate();
  const t = useT();
  const { leadId, token, setLoading } = useContext(GetlifeContext);

  const [options, setOptions] = useState<UpsellOption[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  // Telemetría del upsell (contrato 3.2) de esta entrada: capital inicial, tarjetas,
  // cambios y − / +, para upsell_viewed, los eventos de cada cambio y la decisión.
  const [upsellVisit] = useState(() => new UpsellVisit());
  // Plan «Più scelto»: el capital con el que entró el cliente, fijado en la primera
  // carga (no se mueve aunque luego pida un capital concreto).
  const [recommendedCapital, setRecommendedCapital] = useState<number | null>(null);

  // Capital concreto (30-oct-2026): input con los límites de la rejilla de AGL.
  const [limits, setLimits] = useState<CapitalLimits>(DEFAULT_CAPITAL_LIMITS);
  const [customText, setCustomText] = useState("");
  const [customError, setCustomError] = useState<string | null>(null);
  const [customNotice, setCustomNotice] = useState<string | null>(null);
  const customInputRef = useRef<HTMLInputElement>(null);
  // Tras recalcular, el foco va a la tarjeta del capital pedido (ya seleccionada).
  const [focusPricingId, setFocusPricingId] = useState<string | null>(null);
  // Fecha de efecto (decorrenza): elegida en el paso de preguntas, cambiable aquí.
  // Ventana IT: mañana (DayAvailable) … +1 mes (DaysAvailable). Al cambiarla se
  // parchea la sesión; el select del upsell la manda al gateway.
  const addDaysFromToday = (n: number): Date => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + n);
    return d;
  };
  const minEffectDate = addDaysFromToday(DayAvailable[DOMAIN as keyof typeof DayAvailable] ?? 1);
  const maxEffectDate = addDaysFromToday(DaysAvailable[DOMAIN as keyof typeof DaysAvailable] ?? 30);
  const [effectDate, setEffectDate] = useState<Date>(() => {
    const stored = readOrganicOffer()?.effectDate;
    const parsed = stored ? new Date(stored) : null;
    return parsed && !Number.isNaN(parsed.getTime()) ? parsed : minEffectDate;
  });
  const onEffectDateChange = (value: Date): void => {
    setEffectDate(value);
    const iso = `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(
      value.getDate(),
    ).padStart(2, "0")}`;
    patchOrganicOffer({ effectDate: iso });
  };

  /**
   * Re-prepara las ofertas en el gateway. El offer de sesión existe en el funnel
   * EN VIVO; al REANUDAR por login no está (sessionStorage nueva) → se pide sin
   * inputs y el gateway los rellena del quote guardado. Con `capital` (capital
   * concreto del cliente) las ofertas se centran ahí; el gateway lo ajusta a la
   * rejilla y lo devuelve en `baseCapital`.
   */
  const fetchOptions = async (
    capital?: number,
  ): Promise<{ rated: UpsellOption[]; baseCapital: number | null }> => {
    const offer = readOrganicOffer();
    const repo = new HttpLeadRepository(leadId, token);
    const actuarial =
      null === offer
        ? {}
        : {
            birthDate: offer.birthDate,
            gender: offer.gender,
            smoker: offer.smoker,
            imc: offer.imc ?? null,
            capital: offer.capital,
          };
    const catalog = await repo.getUpsellOptionsNative(
      undefined === capital ? actuarial : { ...actuarial, capital },
    );
    const pricings: UpsellOption[] = Array.isArray(catalog?.pricings) ? catalog.pricings : [];
    setLimits(readCapitalLimits(catalog?.limits));
    // Solo ofertas tarificadas (RATED); descarta out-of-grid/rejected.
    const rated = pricings.filter((p) => undefined === p.rateStatus || "RATED" === p.rateStatus);
    const baseCapital = "number" === typeof catalog?.baseCapital ? catalog.baseCapital : null;
    return { rated, baseCapital };
  };

  useEffect(() => {
    const load = async (): Promise<void> => {
      // NO rebotar a "/" sin offer de sesión (haría bucle con el status upsell_pending).
      const offer = readOrganicOffer();
      setLoading(true);
      try {
        const { rated, baseCapital } = await fetchOptions();
        setOptions(rated);
        // Preselección: el capital inicial del offer (funnel) o, en resume, el capital
        // base del gateway (la oferta central de las 3 ordenadas si no lo manda).
        const initial =
          (null !== offer ? rated.find((p) => p.capital === offer.capital) : undefined) ??
          (null !== baseCapital ? rated.find((p) => p.capital === baseCapital) : undefined) ??
          rated[Math.floor(rated.length / 2)] ??
          rated[0] ??
          null;
        setSelected(initial ? initial.pricingId : null);
        setRecommendedCapital(offer?.capital ?? initial?.capital ?? null);
        if (initial && null !== initial.capital) setCustomText(formatCapitalNumber(initial.capital));
        upsellVisit.view({
          initialCapital: initial?.capital ?? null,
          cards: rated.map((p) => p.capital),
          hasPromotion: rated.some(hasDiscount),
        });
      } catch (error) {
        trackSubmissionError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    };
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (null === focusPricingId) return;
    document.querySelector<HTMLElement>(`[data-pricing-id="${focusPricingId}"]`)?.focus();
    setFocusPricingId(null);
  }, [focusPricingId, options]);

  const fill = (text: string, values: Record<string, string>): string =>
    Object.entries(values).reduce((acc, [k, v]) => acc.split(`{{${k}}}`).join(v), text);

  const limitValues = {
    min: formatCapitalEuro(limits.minCapital),
    max: formatCapitalEuro(limits.maxCapital),
    step: formatCapitalEuro(limits.step),
  };

  const onCustomStep = (direction: 1 | -1): void => {
    upsellVisit.stepperClicked();
    setCustomText(formatCapitalNumber(stepCapital(parseCapitalInput(customText), direction, limits)));
    setCustomError(null);
    setCustomNotice(null);
  };

  /** Capital concreto: valida, re-centra las ofertas en él y lo deja seleccionado. */
  const onApplyCustom = async (event?: FormEvent): Promise<void> => {
    event?.preventDefault();
    // Lo que escribió (null si no es un número), para upsell_custom_applied.
    const requested = parseCapitalInput(customText);
    const check = checkCapital(requested, limits);
    if ("ok" !== check.kind) {
      upsellVisit.customApplied({ requestedCapital: requested, result: CUSTOM_REJECTION[check.kind] });
      setCustomNotice(null);
      setCustomError(
        fill(
          t(
            {
              invalid: "organic.upsell.custom.error.invalid",
              belowMin: "organic.upsell.custom.error.min",
              aboveMax: "organic.upsell.custom.error.max",
            }[check.kind],
          ),
          limitValues,
        ),
      );
      customInputRef.current?.focus();
      return;
    }
    setCustomError(null);
    setCustomText(formatCapitalNumber(check.capital));
    setLoading(true);
    try {
      const { rated, baseCapital } = await fetchOptions(check.capital);
      const target = rated.find((p) => p.capital === (baseCapital ?? check.capital));
      setOptions(rated);
      if (undefined === target || null === target.capital) {
        const fallback = rated[Math.floor(rated.length / 2)];
        setSelected(fallback?.pricingId ?? null);
        upsellVisit.customApplied({ requestedCapital: requested, capital: fallback?.capital ?? null, result: "error" });
        setCustomNotice(null);
        setCustomError(t("organic.upsell.custom.error.request"));
        customInputRef.current?.focus();
        return;
      }
      setSelected(target.pricingId);
      const adjusted = check.adjusted || target.capital !== check.capital;
      upsellVisit.customApplied({
        requestedCapital: requested,
        capital: target.capital,
        result: adjusted ? "adjusted" : "applied",
      });
      // En el funnel en vivo, que una recarga vuelva a este capital y no al inicial.
      patchOrganicOffer({ capital: target.capital });
      setCustomText(formatCapitalNumber(target.capital));
      const capital = formatCapitalEuro(target.capital);
      setCustomNotice(
        adjusted
          ? fill(t("organic.upsell.custom.adjusted"), { ...limitValues, capital })
          : fill(t("organic.upsell.custom.applied"), { capital }),
      );
      setFocusPricingId(target.pricingId);
    } catch (error) {
      trackSubmissionError(error);
      upsellVisit.customApplied({ requestedCapital: requested, result: "error" });
      setCustomNotice(null);
      setCustomError(t("organic.upsell.custom.error.request"));
    } finally {
      setLoading(false);
    }
  };

  const onContinue = async (): Promise<void> => {
    const chosen = options.find((o) => o.pricingId === selected);
    if (null === selected || undefined === chosen) {
      return;
    }
    const offer = readOrganicOffer();
    const effIso = `${effectDate.getFullYear()}-${String(effectDate.getMonth() + 1).padStart(2, "0")}-${String(
      effectDate.getDate(),
    ).padStart(2, "0")}`;
    setLoading(true);
    try {
      const repo = new HttpLeadRepository(leadId, token);
      // El capital ELEGIDO (la tarjeta seleccionada) + la fecha de efecto del
      // selector. Los inputs actuariales SOLO en el funnel en vivo; al REANUDAR por
      // login (sin offer de sesión) los rellena el gateway desde el quote guardado.
      await repo.selectUpsellNative({
        pricingId: selected,
        capital: chosen.capital ?? offer?.capital,
        effectDate: effIso,
        ...(offer?.birthDate
          ? { birthDate: offer.birthDate, gender: offer.gender, smoker: offer.smoker, imc: offer.imc ?? null }
          : {}),
      });
      upsellVisit.decide({
        capital: chosen.capital,
        priceToPay: monthlyEuros(chosen.firstMonthlyCents),
        period: "monthly",
      });
      navigate("/payment/3");
    } catch (error) {
      trackSubmissionError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const selectedCapital = options.find((o) => o.pricingId === selected)?.capital ?? null;
  const bannerSubtitle = t("newUpsell.banner.subtitle").replace(
    "{{capital}}",
    null === selectedCapital ? "" : selectedCapital.toLocaleString("it-IT"),
  );

  // Coberturas (incluidas / escluse): mismo contenido traducido que el legacy
  // (buildScopeItems), replicado aquí para el funnel native.
  const scopeItems = (type: "yes" | "no", count: number, icon: string, color: string) =>
    Array.from({ length: count }, (_, i) => {
      const idx = i + 1;
      return {
        iconElement: (
          <div>
            <GlobalIcon iconName={icon as never} size="XS" color={color} />
          </div>
        ),
        text: `${t(`newUpsell.v3.coverageScope.${type}.${idx}.title`)}. ${t(
          `newUpsell.v3.coverageScope.${type}.${idx}.desc`,
        )}`,
      };
    });
  const listSectionData = [
    {
      iconElement: (
        <div>
          <GlobalIcon iconName="ShieldCheckIcon" size="XS" color="#018565" />
        </div>
      ),
      title: t("newUpsell.listSection.cover"),
      list: scopeItems("yes", 5, "FilledCheckIcon", "#018565"),
    },
    {
      iconElement: (
        <div>
          <GlobalIcon iconName="MoodConfuzedIcon" size="XS" color="#686868" />
        </div>
      ),
      title: t("newUpsell.listSection.notCover"),
      list: scopeItems("no", "it" === DOMAIN ? 5 : 3, "XIcon", "#EA5F5F"),
    },
  ];

  const showTrustpilot = Boolean(GlobalShowTruspilot[DOMAIN as never]);

  return (
    <div className="payment-newUpsellContainer nativeUpsell">
      {/* Hero */}
      <div className="newUpsell--section1">
        <div className="newUpsell-banner--section">
          <BannerPrice title={t("newUpsell.banner.title")} subtitle={bannerSubtitle} homeImage={HOME_IMAGE} />
        </div>

        {/* Selección de capital + resumen de precio */}
        <div className="payment-newUpsellContainer__newsection">
          <div className="w-full max-w-[1120px] mx-auto px-[16px] md:px-[24px] pt-[24px] pb-[8px]">
            <StepBar step={stepList(t)} />
          </div>
          <div className="w-full max-w-[1120px] mx-auto px-[16px] md:px-[24px] pb-[32px] flex flex-col items-center gap-[8px]">
            <Typography
              variant="H2"
              weight="700"
              text={t("organic.upsell.title")}
              className="text-center text-[#424242]"
            />
            <Typography
              variant="Body"
              weight="400"
              text={t("organic.upsell.subtitle")}
              className="text-center text-[#686868] max-w-[640px]"
            />
          </div>

          {/* Comparativa de planes: las 3 opciones de capital como tarjetas de plan */}
          <div className="nativeUpsell__plans" role="radiogroup" aria-label={t("organic.upsell.title")}>
            {options.map((opt) => {
              const isSelected = opt.pricingId === selected;
              const isRecommended = null !== recommendedCapital && opt.capital === recommendedCapital;
              const termCap = null === opt.capital ? null : Math.round(opt.capital / 2);
              const pct = discountPct(opt);
              const planClass = `nativeUpsell__plan${isRecommended ? " nativeUpsell__plan--recommended" : ""}${isSelected ? " nativeUpsell__plan--selected" : ""}`;
              return (
                <button
                  key={opt.pricingId}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  data-pricing-id={opt.pricingId}
                  onClick={() => {
                    if (!isSelected) {
                      upsellVisit.optionSelected({ capital: opt.capital, price: monthlyEuros(opt.firstMonthlyCents) });
                    }
                    setSelected(opt.pricingId);
                    if (null !== opt.capital) setCustomText(formatCapitalNumber(opt.capital));
                    setCustomError(null);
                    setCustomNotice(null);
                  }}
                  className={planClass}
                >
                  {/* Franja superior: recomendado destacado (alineada entre tarjetas) */}
                  <span className={`nativeUpsell__ribbon${isRecommended ? " nativeUpsell__ribbon--on" : ""}`}>
                    {isRecommended && (
                      <>
                        <span aria-hidden="true">★</span>
                        {t("organic.upsell.recommended")}
                      </>
                    )}
                  </span>

                  <span className="nativeUpsell__body">
                    {/* Capital */}
                    <span className="nativeUpsell__capLabel">{t("organic.upsell.capitalLabel")}</span>
                    <span className="nativeUpsell__capValue">{capitalLabel(opt.capital)}</span>

                    {/* Precio */}
                    <span className="nativeUpsell__priceBlock">
                      {hasDiscount(opt) && (
                        <span className="nativeUpsell__priceWas">
                          <span className="nativeUpsell__strike">
                            {perMonthEuro(opt.regularFirstMonthlyCents ?? null)}
                          </span>
                          {null !== pct && <span className="nativeUpsell__pct">−{pct}%</span>}
                        </span>
                      )}
                      <span className="nativeUpsell__priceNow">
                        <span className="nativeUpsell__priceVal">{perMonthEuro(opt.firstMonthlyCents)}</span>
                        <span className="nativeUpsell__priceUnit">{t("organic.upsell.perMonth")}</span>
                      </span>
                    </span>

                    {/* Coberture incluse: DEATH (capital) + MALATTIA_TERMINALE (50%) */}
                    <span className="nativeUpsell__cover">
                      <span className="nativeUpsell__coverRow">
                        <span className="nativeUpsell__coverName">
                          <GlobalIcon iconName="FilledCheckIcon" size="XS" color="#018565" />
                          {t("calculatorPage.textCoverageDeath")}
                        </span>
                        <span className="nativeUpsell__coverVal">{capitalLabel(opt.capital)}</span>
                      </span>
                      <span className="nativeUpsell__coverRow">
                        <span className="nativeUpsell__coverName">
                          <GlobalIcon iconName="FilledCheckIcon" size="XS" color="#018565" />
                          {t("calculatorPage.textAdditionalProtectionTerminalIllness")}
                        </span>
                        <span className="nativeUpsell__coverVal">{capitalLabel(termCap)}</span>
                      </span>
                    </span>

                    <span className="nativeUpsell__spacer" />

                    {/* Estado de selección (la tarjeta entera es seleccionable) */}
                    <span className={`nativeUpsell__pill${isSelected ? " nativeUpsell__pill--selected" : ""}`}>
                      {isSelected ? `✓ ${t("organic.upsell.selected")}` : t("organic.upsell.choose")}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* Capital concreto (30-oct-2026): las 3 tarjetas quedan como sugerencias;
              aquí el cliente escribe el capital que quiere (con − / + de un paso)
              y las ofertas se re-centran en él sin recargar. */}
          <div className="nativeUpsell__custom">
            <form className="nativeUpsell__customCard" onSubmit={(e) => void onApplyCustom(e)} noValidate>
              <p className="nativeUpsell__customTitle">{t("organic.upsell.custom.title")}</p>
              <label htmlFor="nativeUpsell-customCapital" className="nativeUpsell__customLabel">
                {t("organic.upsell.custom.label")}
              </label>
              <div className="nativeUpsell__customRow">
                <button
                  type="button"
                  className="nativeUpsell__stepper"
                  onClick={() => onCustomStep(-1)}
                  disabled={(parseCapitalInput(customText) ?? limits.minCapital) <= limits.minCapital}
                  aria-label={fill(t("organic.upsell.custom.decrease"), limitValues)}
                  aria-controls="nativeUpsell-customCapital"
                >
                  −
                </button>
                <div className={`nativeUpsell__customField${customError ? " nativeUpsell__customField--error" : ""}`}>
                  <input
                    ref={customInputRef}
                    id="nativeUpsell-customCapital"
                    // Nombre para field_completed (el id lleva guion y el registro de campos lo descarta).
                    name="upsell_custom_capital"
                    className="nativeUpsell__customInput"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={customText}
                    onChange={(e) => {
                      setCustomText(e.target.value);
                      setCustomError(null);
                      setCustomNotice(null);
                    }}
                    onBlur={() => {
                      const parsed = parseCapitalInput(customText);
                      if (null !== parsed) setCustomText(formatCapitalNumber(parsed));
                    }}
                    aria-invalid={null !== customError}
                    aria-describedby={`nativeUpsell-customHint${customError ? " nativeUpsell-customError" : ""}`}
                  />
                  <span className="nativeUpsell__customSuffix" aria-hidden="true">
                    €
                  </span>
                </div>
                <button
                  type="button"
                  className="nativeUpsell__stepper"
                  onClick={() => onCustomStep(1)}
                  disabled={(parseCapitalInput(customText) ?? limits.minCapital) >= limits.maxCapital}
                  aria-label={fill(t("organic.upsell.custom.increase"), limitValues)}
                  aria-controls="nativeUpsell-customCapital"
                >
                  +
                </button>
              </div>
              <p id="nativeUpsell-customHint" className="nativeUpsell__customHint">
                {fill(t("organic.upsell.custom.hint"), limitValues)}
              </p>
              {customError && (
                <p id="nativeUpsell-customError" className="nativeUpsell__customError" role="alert">
                  {customError}
                </p>
              )}
              <p className="nativeUpsell__customNotice" role="status" aria-live="polite">
                {customNotice ?? ""}
              </p>
              <button type="submit" className="nativeUpsell__customApply">
                {t("organic.upsell.custom.apply")}
              </button>
            </form>
          </div>

          {/* Fecha de efecto (decorrenza): cambiable aquí; por defecto la elegida
              en el paso de preguntas (o mañana). Centrada con clases SCSS propias:
              con utilidades de Tailwind (flex justify-center) salía pegada a la
              izquierda en el build native. */}
          <div className="nativeUpsell__effectDate">
            <div className="nativeUpsell__effectDateCard">
            <DatePicker
              initialDate={effectDate}
              minDate={minEffectDate}
              maxDate={maxEffectDate}
              locale="it"
              notShowIpa
              title={t("upsell.DatePicker.title") || "Da quando vuoi essere assicurato?"}
              subtext1={t("upsell.DatePicker.subtitle1") || "Puoi iniziare la copertura già da domani."}
              subtext2={t("upsell.DatePicker.subtitle2") || "Oppure scegli una data entro il prossimo mese."}
              subtext3={t("upsell.DatePicker.subtitle3")}
              textToday={t("upsell.DatePicker.textToday") || "Oggi"}
              textTomorrow={t("upsell.DatePicker.textTomorrow") || "Domani"}
              textAfterTomorrow={t("upsell.DatePicker.textAfterTomorrow") || "Dopodomani"}
              overlayButtonLabel={t("productQuestions.overlay.button") || "Conferma"}
              onValueChange={onEffectDateChange}
            />
            </div>
          </div>

          {/* Nota del 50% malattia terminale + CTA */}
          <div className="w-full max-w-[680px] mx-auto px-[16px] md:px-[24px] pb-[40px] flex flex-col items-center gap-[16px]">
            <p className="text-[12px] leading-[1.4] text-[#686868] text-center">
              {t("calculatorPage.textAdditionalProtectionInfoTerminal")}
            </p>
            <div className="w-full max-w-[325px]">
              <ButtonDS
                label={t("organic.upsell.continue")}
                onClick={onContinue}
                rightIcon="ArrowRightIcon"
                disabled={null === selected}
                className="w-full"
              />
            </div>
            <i className="text-[#686868] text-[14px] text-center">{t("organic.upsell.cancelAnytime")}</i>
            <ITComplianceOnly>
              <PriceTransparencyNote translate={t} />
            </ITComplianceOnly>
          </div>
        </div>
      </div>

      {/* Trustpilot */}
      {showTrustpilot && (
        <div className="payment-newUpsellContainer__newsection">
          <TrustpilotReviews
            comments={Truspilot(t)}
            excelentText={t("newUpsell.truspilotSection.excelentText")}
            rating={4.8}
            title={t("newUpsell.truspilotSection.title")}
            trustpilotText={t("newUpsell.truspilotSection.trustpilotText")}
          />
        </div>
      )}

      {/* Coberturas */}
      <div className="payment-newUpsellContainer__newsection">
        <div className="containerSection">
          <ITComplianceOnly>
            <div className="nativeUpsell__centered">
              <IntermediaryBadge translate={t} />
            </div>
          </ITComplianceOnly>
          <ListSection title={t("newUpsell.listSection.title")} list={listSectionData} />
        </div>
      </div>

      {/* Beneficios */}
      <div className="payment-newUpsellContainer__newsection">
        <UpsellIconSection title={t("newUpsell.iconSection.title")} data={IconSection(t) as never} />
      </div>

      {/* Contenido estático */}
      <div className="payment-newUpsellContainer__newsection">
        <StaticImgText
          image={t("newUpsell.staticImgText.img")}
          title={t("newUpsell.staticImgText.title")}
          titleMobile={t("newUpsell.staticImgText.titleMobile")}
          paragraphFirst1={t("newUpsell.staticImgText.paragraphFirst1")}
          paragraphFirst2={t("newUpsell.staticImgText.paragraphFirst2")}
          paragraphFirst3={t("newUpsell.staticImgText.paragraphFirst3")}
          paragraphFirst4={t("newUpsell.staticImgText.paragraphFirst4")}
          paragraphSecond1={t("newUpsell.staticImgText.paragraphSecond1")}
          paragraphSecond2={t("newUpsell.staticImgText.paragraphSecond2")}
          paragraphSecond3={t("newUpsell.staticImgText.paragraphSecond3")}
          paragraphSecond4={t("newUpsell.staticImgText.paragraphSecond4")}
          paragraphSecond5={t("newUpsell.staticImgText.paragraphSecond5")}
        />
      </div>

      {/* Documentación precontractual + derecho de recesso (IT) */}
      <ITComplianceOnly>
        <div className="payment-newUpsellContainer__newsection">
          <div className="nativeUpsell__legal">
            <PrecontractualDocs translate={t} />
            <WithdrawalRightNotice translate={t} />
          </div>
        </div>
      </ITComplianceOnly>
    </div>
  );
}
